import { useEffect } from 'react';

const DEFAULT_IMAGE = `${window.location.origin}/og-image.png`;

function setMeta(selector, attr, value) {
    if (!value) return;
    let tag = document.head.querySelector(selector);
    if (!tag) {
        tag = document.createElement('meta');
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) tag.setAttribute(match[1], match[2]);
        document.head.appendChild(tag);
    }
    tag.setAttribute(attr, value);
}

function setCanonical(url) {
    if (!url) return;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }
    link.setAttribute('href', url);
}

function setThemeColor(color) {
    let tag = document.head.querySelector('meta[name="theme-color"]');
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'theme-color');
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', color);
}

export function useShareMetadata({ title, description, image, url, type = 'website', tier } = {}) {
    useEffect(() => {
        const resolvedImage = image || DEFAULT_IMAGE;
        const resolvedUrl = url || window.location.href;

        if (title) document.title = title;

        // Primary
        setMeta('meta[name="title"]', 'content', title);
        setMeta('meta[name="description"]', 'content', description);
        setMeta('meta[name="robots"]', 'content', 'index, follow');

        // Open Graph
        setMeta('meta[property="og:type"]', 'content', type);
        setMeta('meta[property="og:url"]', 'content', resolvedUrl);
        setMeta('meta[property="og:title"]', 'content', title);
        setMeta('meta[property="og:description"]', 'content', description);
        setMeta('meta[property="og:image"]', 'content', resolvedImage);
        setMeta('meta[property="og:image:secure_url"]', 'content', resolvedImage);
        setMeta('meta[property="og:image:width"]', 'content', '1200');
        setMeta('meta[property="og:image:height"]', 'content', '630');
        setMeta('meta[property="og:image:alt"]', 'content', title);
        setMeta('meta[property="og:site_name"]', 'content', 'Aluvo');
        setMeta('meta[property="og:locale"]', 'content', 'en_US');

        // Twitter / X
        setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
        setMeta('meta[name="twitter:site"]', 'content', '@aluvohq');
        setMeta('meta[name="twitter:creator"]', 'content', '@aluvohq');
        setMeta('meta[name="twitter:url"]', 'content', resolvedUrl);
        setMeta('meta[name="twitter:title"]', 'content', title);
        setMeta('meta[name="twitter:description"]', 'content', description);
        setMeta('meta[name="twitter:image"]', 'content', resolvedImage);
        setMeta('meta[name="twitter:image:alt"]', 'content', title);

        // Discord / iMessage / Slack theme accent
        const themeColor = tier === 'legendary' ? '#D4AF37' : tier === 'gold' ? '#FFD700' : tier === 'silver' ? '#C0C0C0' : '#D4AF37';
        setThemeColor(themeColor);

        setCanonical(resolvedUrl);
    }, [title, description, image, url, type, tier]);
}

export function buildOgImageUrl({ title, subtitle, username, tier, theme = 'cinematic' }) {
    const params = new URLSearchParams({
        title: title || 'Aluvo',
        subtitle: subtitle || 'Verified movie & TV identity',
        username: username || '',
        tier: tier || '',
        theme,
    });
    return `${window.location.origin}/og-image.png?${params.toString()}`;
}